import { FunctionalComponent } from "preact";
import { useState } from "preact/hooks";
import { Bookmark } from "../utils/api";

interface BookmarkDetailProps {
  bookmark: Bookmark;
  onClose: () => void;
  onUpdate: (id: string, data: Partial<Bookmark>) => Promise<void>;
  onDelete: (id: string) => void;
  onToggleReadLater?: (bookmark: Bookmark) => void;
  onArchive?: (bookmark: Bookmark) => void;
}

export const BookmarkDetail: FunctionalComponent<BookmarkDetailProps> = ({
  bookmark,
  onClose,
  onUpdate,
  onDelete,
  onToggleReadLater,
  onArchive,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [notes, setNotes] = useState(bookmark.notes || "");
  const [saving, setSaving] = useState(false);

  const handleSaveNotes = async () => {
    setSaving(true);
    try {
      await onUpdate(bookmark.id, { notes });
    } finally {
      setSaving(false);
      setIsEditing(false);
    }
  };

  const handleToggleReadLater = async () => {
    if (onToggleReadLater) {
      onToggleReadLater(bookmark);
    } else {
      await onUpdate(bookmark.id, { readLater: !bookmark.readLater });
    }
  };

  const handleToggleRead = async () => {
    await onUpdate(bookmark.id, { isRead: !bookmark.isRead });
  };

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return "Never";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div class="fixed inset-0 z-50 overflow-y-auto">
      <div class="flex min-h-screen items-center justify-center p-4">
        <div
          class="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onClose}
        />
        <div class="relative bg-white dark:bg-stone-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-stone-200 dark:border-stone-800">
          <div class="flex items-start justify-between p-6 border-b border-stone-100 dark:border-stone-800/50 sticky top-0 bg-white/90 dark:bg-stone-900/90 backdrop-blur-md z-10">
            <div class="flex-1 min-w-0 pr-6">
              <a
                href={bookmark.url}
                target="_blank"
                rel="noopener noreferrer"
                class="text-2xl font-display font-bold text-stone-900 dark:text-stone-50 hover:text-primary-600 dark:hover:text-primary-400 transition-colors leading-tight block mb-1"
              >
                {bookmark.title}
              </a>
              <a
                href={bookmark.url}
                target="_blank"
                rel="noopener noreferrer"
                class="text-sm text-stone-400 dark:text-stone-500 truncate block hover:text-primary-500 transition-colors"
              >
                {bookmark.url}
              </a>
            </div>
            <button
              onClick={onClose}
              class="p-2 rounded-full text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800 transition-all"
            >
              <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div class="p-8 space-y-10">
            {bookmark.description && (
              <div class="space-y-2">
                <h4 class="text-xs font-bold uppercase tracking-widest text-stone-400 dark:text-stone-500">
                  Description
                </h4>
                <p class="text-lg font-serif text-stone-700 dark:text-stone-200 leading-relaxed">{bookmark.description}</p>
              </div>
            )}

            {bookmark.highlight && (
              <div class="bg-amber-50/50 dark:bg-amber-900/10 border-l-4 border-amber-400 p-6 rounded-r-2xl">
                <h4 class="text-xs font-bold uppercase tracking-widest text-amber-700 dark:text-amber-400 mb-3 flex items-center gap-2">
                  <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                  </svg>
                  Highlight
                </h4>
                <blockquote class="text-xl font-serif text-stone-800 dark:text-stone-100 italic leading-relaxed">
                  "{bookmark.highlight}"
                </blockquote>
              </div>
            )}

            <div class="flex flex-wrap gap-3">
              <button
                onClick={handleToggleReadLater}
                class={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all shadow-sm ${
                  bookmark.readLater
                    ? "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800"
                    : "bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 hover:bg-stone-200 dark:hover:bg-stone-700 border border-stone-200 dark:border-stone-700"
                }`}
              >
                <svg class="h-4 w-4" fill={bookmark.readLater ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {bookmark.readLater ? "In Read Later" : "Read Later"}
              </button>

              <button
                onClick={handleToggleRead}
                class={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all shadow-sm ${
                  bookmark.isRead
                    ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800"
                    : "bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 hover:bg-stone-200 dark:hover:bg-stone-700 border border-stone-200 dark:border-stone-700"
                }`}
              >
                <svg class="h-4 w-4" fill={bookmark.isRead ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
                {bookmark.isRead ? "Read" : "Mark as Read"}
              </button>

              {onArchive && (
                <button
                  onClick={() => onArchive(bookmark)}
                  class={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all shadow-sm ${
                    bookmark.isArchived
                      ? "bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 border border-primary-200 dark:border-primary-800"
                      : "bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 hover:bg-stone-200 dark:hover:bg-stone-700 border border-stone-200 dark:border-stone-700"
                  }`}
                >
                  <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                  </svg>
                  {bookmark.isArchived ? "Read Archive" : "Archive Page"}
                </button>
              )}
            </div>

            {bookmark.tags && (
              <div class="space-y-3">
                <h4 class="text-xs font-bold uppercase tracking-widest text-stone-400 dark:text-stone-500">
                  Tags
                </h4>
                <div class="flex flex-wrap gap-2">
                  {(() => {
                    try {
                      const tags = JSON.parse(bookmark.tags);
                      return Array.isArray(tags) ? tags.map((tag: string) => (
                        <span
                          key={tag}
                          class="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400 border border-stone-200 dark:border-stone-700"
                        >
                          {tag}
                        </span>
                      )) : null;
                    } catch {
                      return null;
                    }
                  })()}
                </div>
              </div>
            )}

            <div class="space-y-4">
              <div class="flex items-center justify-between">
                <h4 class="text-xs font-bold uppercase tracking-widest text-stone-400 dark:text-stone-500">
                  Notes
                </h4>
                {!isEditing && (
                  <button
                    onClick={() => setIsEditing(true)}
                    class="text-sm font-semibold text-primary-600 dark:text-primary-400 hover:text-primary-700 transition-colors"
                  >
                    Edit Notes
                  </button>
                )}
              </div>

              {isEditing ? (
                <div class="space-y-3">
                  <textarea
                    value={notes}
                    onInput={(e) => setNotes((e.target as HTMLTextAreaElement).value)}
                    rows={6}
                    class="w-full px-4 py-3 border border-stone-200 dark:border-stone-800 rounded-xl bg-white dark:bg-stone-950 text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all font-serif text-lg"
                    placeholder="Add your notes..."
                  />
                  <div class="flex justify-end gap-3">
                    <button
                      onClick={() => {
                        setIsEditing(false);
                        setNotes(bookmark.notes || "");
                      }}
                      class="px-4 py-2 text-sm font-medium text-stone-600 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800 rounded-full transition-all"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveNotes}
                      disabled={saving}
                      class="px-5 py-2 text-sm font-bold bg-primary-600 text-white rounded-full hover:bg-primary-700 disabled:opacity-50 shadow-sm transition-all"
                    >
                      {saving ? "Saving..." : "Save Notes"}
                    </button>
                  </div>
                </div>
              ) : (
                <div class="prose prose-stone dark:prose-invert max-w-none">
                  <p class="text-lg font-serif text-stone-800 dark:text-stone-100 whitespace-pre-wrap leading-relaxed">
                    {bookmark.notes || (
                      <span class="text-stone-400 dark:text-stone-500 italic">
                        No notes yet
                      </span>
                    )}
                  </p>
                </div>
              )}
            </div>

            <div class="pt-8 border-t border-stone-100 dark:border-stone-800/50">
              <div class="grid grid-cols-2 gap-4 text-xs font-medium text-stone-400 dark:text-stone-500">
                <div class="space-y-1">
                  <p class="uppercase tracking-wider text-[10px] font-bold">Created</p>
                  <p>{formatDate(bookmark.createdAt)}</p>
                </div>
                <div class="space-y-1">
                  <p class="uppercase tracking-wider text-[10px] font-bold">Updated</p>
                  <p>{formatDate(bookmark.updatedAt)}</p>
                </div>
                {bookmark.lastAccessed && (
                  <div class="space-y-1 col-span-2">
                    <p class="uppercase tracking-wider text-[10px] font-bold">Last Accessed</p>
                    <p>{formatDate(bookmark.lastAccessed)}</p>
                  </div>
                )}
              </div>
            </div>

            <div class="flex justify-end gap-3 pt-6 border-t border-stone-100 dark:border-stone-800/50">
              <button
                onClick={() => {
                  if (confirm("Are you sure you want to delete this bookmark?")) {
                    onDelete(bookmark.id);
                    onClose();
                  }
                }}
                class="px-5 py-2.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition-all font-semibold text-sm"
              >
                Delete Bookmark
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
